import { useEffect, useRef, useState } from "react";
import { useData, useToast } from "../context/Data";
import { api, errMsg } from "../lib/api";
import { Bar } from "../components/common";
import { eur, eur0 } from "../lib/fmt";
import { tierInfo } from "./Inicio";
import { UNLOCK_LABEL } from "./Vip";

const LIMIT_KEY = { survey: "surveys_per_day", video: "videos_per_day", game: "games_per_day", site: "sites_per_day" };
const UNLOCK_IDX = { survey: 0, video: 1, game: 2, site: 3 };

function useTimer(active, seconds, onDone) {
  const [pct, setPct] = useState(0);
  const cb = useRef(onDone);
  cb.current = onDone;
  useEffect(() => {
    if (!active) { setPct(0); return; }
    const start = Date.now();
    const id = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / (seconds * 1000)) * 100);
      setPct(p);
      if (p >= 100) { clearInterval(id); cb.current?.(); }
    }, 200);
    return () => clearInterval(id);
  }, [active, seconds]);
  return pct;
}

function useActivity(type) {
  const { run } = useData();
  const { toast } = useToast();
  const [sess, setSess] = useState(null);
  const start = async () => {
    try { const { data } = await api.post("/me/activities/start", { type }); setSess(data); } catch (e) { toast(`⚠️ ${errMsg(e)}`, true); }
  };
  const complete = async (extra = {}) => {
    const s = sess;
    const res = await run(() => api.post("/me/activities/complete", { session_id: s.session_id, ...extra }));
    if (type !== "game") setSess(null);
    return res;
  };
  return { sess, setSess, start, complete };
}

function Card({ type, icon, title, desc, d, children, testid }) {
  const locked = d.tier_idx < UNLOCK_IDX[type];
  const tier = d.config.tiers[UNLOCK_IDX[type]];
  const count = d.counts[`${type}_today`];
  const limit = d.config.limits[LIMIT_KEY[type]];
  return (
    <article className={`activity-card ${locked ? "locked" : ""}`} data-testid={testid}>
      <div className="activity-icon">{icon}</div><h3>{title}</h3><p>{desc}</p>
      <span className={`badge ${locked ? "" : "green"}`}>{locked ? `🔒 ${tier.name} • ${tier.refs} amigos` : `${tier.icon} ${tier.name}`}</span>
      <div style={{ height: 12 }} />
      {children(locked, count >= limit)}
      <div className="activity-message" data-testid={`${testid}-count`}>{count}/{limit} hoy</div>
    </article>
  );
}

function Survey({ d }) {
  const a = useActivity("survey");
  const [ans, setAns] = useState([]);
  const [ready, setReady] = useState(false);
  const pct = useTimer(!!a.sess, a.sess?.min_seconds || 8, () => setReady(true));
  const begin = async () => { setAns([]); setReady(false); await a.start(); };
  return (
    <Card type="survey" icon="📝" title="Encuestas" desc="Tu actividad inicial. Responde con sinceridad: solo cuentan las encuestas validadas por el proveedor." d={d} testid="activity-survey">
      {(locked, full) => !a.sess ? (
        <button className="primary" disabled={locked || full} onClick={begin} data-testid="survey-start-btn">{full ? "Límite diario alcanzado" : `Empezar encuesta · +${eur(d.config.rewards.survey)}`}</button>
      ) : (
        <div data-testid="survey-form">
          {a.sess.questions.map((q, i) => (
            <div className="survey-q" key={i}><p>{i + 1}. {q.q}</p><div className="opt-row">{q.options.map((o, j) => <button key={o} className={`opt ${ans[i] === j ? "sel" : ""}`} onClick={() => { const n = [...ans]; n[i] = j; setAns(n); }} data-testid={`survey-q${i}-opt${j}`}>{o}</button>)}</div></div>
          ))}
          <Bar pct={pct} small />
          <button className="primary" disabled={!ready || ans.filter((x) => x !== undefined).length < a.sess.questions.length} onClick={() => a.complete({ answers: ans })} data-testid="survey-submit-btn">{ready ? "✅ Enviar encuesta" : "Lee y responde con calma…"}</button>
        </div>
      )}
    </Card>
  );
}

function Video({ d }) {
  const a = useActivity("video");
  const [ready, setReady] = useState(false);
  const pct = useTimer(!!a.sess, a.sess?.min_seconds || 10, () => setReady(true));
  useEffect(() => { if (ready && a.sess) { setReady(false); a.complete(); } }, [ready]); // eslint-disable-line
  return (
    <Card type="video" icon="📺" title="Ver vídeos" desc="Vídeo recompensado de 10 segundos. Se abona al completar la reproducción." d={d} testid="activity-video">
      {(locked, full) => (<>
        <button className="primary" disabled={locked || full || !!a.sess} onClick={a.start} data-testid="video-start-btn">{a.sess ? "Reproduciendo…" : full ? "Límite diario alcanzado" : `▶ Ver vídeo · +${eur(d.config.rewards.video)}`}</button>
        <div className={`demo-player ${a.sess ? "playing" : ""}`} aria-label="Reproductor de vídeo"><span>◉ REPRODUCIENDO</span></div>
        <Bar pct={pct} small testid="video-progress" />
      </>)}
    </Card>
  );
}

function Game({ d }) {
  const a = useActivity("game");
  const [result, setResult] = useState(null);
  const pick = async (i) => {
    const res = await a.complete({ chest: i });
    if (res) setResult({ pick: i, winning: res.winning, won: res.won });
    setTimeout(() => { a.setSess(null); setResult(null); }, 2200);
  };
  return (
    <Card type="game" icon="🎮" title="Minijuego: elige un cofre" desc={`Elige uno de tres cofres para encontrar la estrella (hasta ${eur(d.config.rewards.game)} por partida premiada). El resultado se decide en el servidor.`} d={d} testid="activity-game">
      {(locked, full) => (<>
        {!a.sess && <button className="primary" disabled={locked || full} onClick={a.start} data-testid="game-start-btn">{full ? "Límite diario alcanzado" : "🎲 Empezar partida"}</button>}
        {a.sess && <div className="game-options">{[0, 1, 2].map((i) => {
          const cls = result ? (i === result.winning ? "win" : "lose") : "";
          return <button key={i} disabled={!!result} className={cls} onClick={() => pick(i)} aria-label={`Cofre ${i + 1}`} data-testid={`game-chest-${i}`}>{result && i === result.winning ? "⭐" : "🎁"}</button>;
        })}</div>}
      </>)}
    </Card>
  );
}

function Site({ d }) {
  const a = useActivity("site");
  const [ready, setReady] = useState(false);
  const pct = useTimer(!!a.sess, a.sess?.min_seconds || 10, () => setReady(true));
  useEffect(() => { if (ready && a.sess) { setReady(false); a.complete(); } }, [ready]); // eslint-disable-line
  return (
    <Card type="site" icon="🌐" title="Descubrir páginas" desc="Visita una página de una campaña legítima durante 10 segundos. Nunca incentivamos clics en anuncios." d={d} testid="activity-site">
      {(locked, full) => (<>
        <button className="primary" disabled={locked || full || !!a.sess} onClick={a.start} data-testid="site-start-btn">{a.sess ? "Visitando…" : full ? "Límite diario alcanzado" : `🌍 Visitar página · +${eur(d.config.rewards.site)}`}</button>
        {a.sess && <div className="site-frame" data-testid="site-frame"><span className="demo-mark">VISITANDO</span><h4>{a.sess.campaign.title}</h4><p className="tiny muted">{a.sess.campaign.description}</p></div>}
        <Bar pct={pct} small testid="site-progress" />
      </>)}
    </Card>
  );
}

export default function Actividades() {
  const { d } = useData();
  const t = tierInfo(d);
  return (
    <>
      <div className="page-intro"><div className="eyebrow">NUEVAS MANERAS DE PARTICIPAR</div><h1>🎮 Actividades por nivel</h1><p className="lead">Las encuestas están disponibles desde Principiante. Desbloquea vídeos, minijuegos y visitas a páginas al aumentar XP <b>y</b> conseguir amigos verificados.</p></div>
      <div className="step-gate"><strong data-testid="unlock-caption">{t.next ? `🔒 ${t.gapText}.` : "💎 Tienes todas las actividades desbloqueadas."}</strong>
        <Bar pct={t.next ? (t.xpPct + t.refPct) / 2 : 100} small />
        <div className="soft-line" data-testid="unlock-details">{t.next ? `${d.valid_refs}/${t.next.refs} referidos • ${t.xp}/${t.next.xp} XP` : "Nivel máximo alcanzado"}</div></div>
      <div className="unlock-grid">
        {d.config.tiers.map((x, i) => (
          <div key={x.key} className={`unlock-card ${i <= d.tier_idx ? "is-open" : ""}`} data-testid={`unlock-${x.key}`}><div className="icon">{x.icon}</div><h3>{x.name}</h3><p>{UNLOCK_LABEL[x.unlock]}. {i === 0 ? "Desde el registro." : `Requiere ${x.xp} XP y ${x.refs} amigos verificados.`}</p><span className={`badge ${i <= d.tier_idx ? "green" : ""}`}>{i <= d.tier_idx ? "✅ Desbloqueado" : "🔒 Bloqueado"}</span></div>
        ))}
      </div>
      <div className="section-title"><h2>🌟 Elige tu actividad</h2><span className="badge green">RECOMPENSAS VERIFICADAS</span></div>
      <div className="activity-grid"><Survey d={d} /><Video d={d} /><Game d={d} /><Site d={d} /></div>
      <div className="notice" style={{ marginTop: 18 }}>✅ Cada actividad se valida en el servidor antes de abonarse ({eur0(d.config.rewards.survey)} por encuesta). Refrescar o repetir una actividad nunca duplica la recompensa.</div>
    </>
  );
}
